import type { JSX } from 'react';
import { Button } from '@/app/ui/controls';
import { verdictMessage } from '@/core/validation/decode';
import type { OutputFormat } from '@/core/project/types';
import type { ExportJob } from './useExportJob';
import styles from './ExportBar.module.css';

/**
 * Кнопка сохранения и статус проверки.
 *
 * Ссылка на скачивание появляется только после того, как готовый файл
 * прочитан независимым декодером и совпал с ожидаемым содержимым.
 */

const FORMAT_LABEL: Record<OutputFormat, string> = { png: 'PNG', jpeg: 'JPEG', svg: 'SVG' };

export type ExportBarProps = {
  job: ExportJob;
  ready: boolean;
  format: OutputFormat;
  onExport: () => void;
};

function bytes(value: number): string {
  if (value < 1024) return `${value} Б`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} КБ`;
  return `${(value / 1024 / 1024).toFixed(2)} МБ`;
}

export function ExportBar({ job, ready, format, onExport }: ExportBarProps): JSX.Element {
  const { state } = job;
  const busy = state.phase === 'rendering' || state.phase === 'verifying';

  return (
    <div className={styles.bar}>
      <div className={styles.actions}>
        <Button variant="primary" onClick={onExport} disabled={!ready || busy}>
          {busy ? 'Готовим файл…' : `Подготовить ${FORMAT_LABEL[format]}`}
        </Button>
        {busy ? (
          <Button variant="ghost" onClick={job.cancel}>
            Отменить
          </Button>
        ) : null}
        {state.phase === 'ready' ? (
          <a className={styles.download} href={state.url} download={state.fileName}>
            Скачать {state.fileName}
          </a>
        ) : null}
      </div>

      <div className={styles.status} role="status" aria-live="polite">
        {state.phase === 'idle' && !ready ? (
          <span className={styles.muted}>
            Файл станет доступен, когда в макете не останется ошибок.
          </span>
        ) : null}
        {state.phase === 'idle' && ready ? (
          <span className={styles.muted}>
            Файл создаётся на вашем устройстве. Проверка считывания выполняется до скачивания.
          </span>
        ) : null}
        {state.phase === 'rendering' ? <span>Строим файл…</span> : null}
        {state.phase === 'verifying' ? <span>Читаем готовый файл независимым декодером…</span> : null}
        {state.phase === 'cancelled' ? <span className={styles.muted}>Подготовка отменена.</span> : null}
        {state.phase === 'failed' ? (
          <span className={styles.error}>Ошибка: {state.message}</span>
        ) : null}
        {state.phase === 'ready' ? (
          <span className={verdictMessage(state.verdict).level === 'ok' ? styles.ok : styles.warn}>
            {verdictMessage(state.verdict).text} {state.file.mime}, {state.file.widthPx}×
            {state.file.heightPx} px, {bytes(state.file.bytes)}.
          </span>
        ) : null}
      </div>

      {state.phase === 'ready' ? (
        <p className={styles.disclaimer}>
          Проверка одного цифрового файла не гарантирует сканирование после печати, уменьшения,
          бликов и съёмки под углом.
        </p>
      ) : null}
    </div>
  );
}
